<script setup>
import { computed, ref, watch } from 'vue';
import { debounce } from '@chatwoot/utils';
import { useI18n } from 'vue-i18n';
import { useStore, useStoreGetters } from 'dashboard/composables/store';

import Icon from 'dashboard/components-next/icon/Icon.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import WhatsappTemplate from './WhatsappTemplate.vue';

const PAGE_SIZE = 5;

const props = defineProps({
  inboxId: {
    type: Number,
    required: true,
  },
  selectedContact: {
    type: Object,
    default: null,
  },
});

const emit = defineEmits(['sendMessage']);

const { t } = useI18n();
const store = useStore();
const getters = useStoreGetters();

const searchQuery = ref('');
const selectedTemplate = ref(null);
const showTemplatesMenu = ref(false);
const currentPage = ref(1);

const whatsAppTemplateMessages = computed(
  () => getters['whatsappTemplates/getPickerTemplates'].value
);

watch(
  () => props.inboxId,
  inboxId => {
    if (inboxId) {
      store.dispatch('whatsappTemplates/fetchForPicker', { inboxId }).catch(() => {});
    }
  },
  { immediate: true }
);

const totalPages = computed(() =>
  Math.max(1, Math.ceil(whatsAppTemplateMessages.value.length / PAGE_SIZE))
);

const paginatedTemplates = computed(() => {
  const start = (currentPage.value - 1) * PAGE_SIZE;
  return whatsAppTemplateMessages.value.slice(start, start + PAGE_SIZE);
});

watch(
  searchQuery,
  debounce(async newQuery => {
    currentPage.value = 1;
    await store
      .dispatch('whatsappTemplates/fetchForPicker', {
        inboxId: props.inboxId,
        name: newQuery || undefined,
      })
      .catch(() => {});
  }, 300)
);

const getTemplateBody = template => {
  return (
    template.components.find(component => component.type === 'BODY')?.text || ''
  );
};

const handleTriggerClick = () => {
  searchQuery.value = '';
  currentPage.value = 1;
  showTemplatesMenu.value = !showTemplatesMenu.value;
};

const handleTemplateClick = template => {
  selectedTemplate.value = template;
  showTemplatesMenu.value = false;
};

const handleBack = () => {
  selectedTemplate.value = null;
  showTemplatesMenu.value = true;
};

const handleSendMessage = template => {
  emit('sendMessage', template);
  selectedTemplate.value = null;
};
</script>

<template>
  <div class="relative">
    <Button
      icon="i-ri-whatsapp-line"
      :label="t('COMPOSE_NEW_CONVERSATION.FORM.WHATSAPP_OPTIONS.LABEL')"
      color="slate"
      size="sm"
      :disabled="selectedTemplate"
      class="!text-xs font-medium"
      @click="handleTriggerClick"
    />
    <div
      v-if="showTemplatesMenu"
      class="absolute top-full mt-1.5 ltr:left-0 rtl:right-0 flex flex-col gap-2 p-4 items-center w-[21.875rem] h-auto bg-n-solid-2 border border-n-strong shadow-sm rounded-lg"
    >
      <div class="relative w-full">
        <Icon
          icon="i-lucide-search"
          class="absolute size-3.5 top-2 ltr:left-3 rtl:right-3"
        />
        <input
          v-model="searchQuery"
          type="search"
          :placeholder="
            t(
              'COMPOSE_NEW_CONVERSATION.FORM.WHATSAPP_OPTIONS.SEARCH_PLACEHOLDER'
            )
          "
          class="w-full h-8 py-2 ltr:pl-10 rtl:pr-10 ltr:pr-2 rtl:pl-2 text-sm reset-base outline-none border-none rounded-lg bg-n-alpha-black2 dark:bg-n-solid-1 text-n-slate-12"
        />
      </div>
      <div class="w-full max-h-72 overflow-y-auto flex flex-col gap-1">
        <div
          v-for="template in paginatedTemplates"
          :key="template.id"
          class="flex flex-col gap-2 p-2 w-full rounded-lg cursor-pointer dark:hover:bg-n-alpha-3 hover:bg-n-alpha-1"
          @click="handleTemplateClick(template)"
        >
          <span class="text-sm text-n-slate-12">{{ template.name }}</span>
          <p class="mb-0 text-xs leading-5 text-n-slate-11 line-clamp-2">
            {{ getTemplateBody(template) }}
          </p>
        </div>
        <template v-if="whatsAppTemplateMessages.length === 0">
          <p class="pt-2 w-full text-sm text-n-slate-11">
            {{
              t('COMPOSE_NEW_CONVERSATION.FORM.WHATSAPP_OPTIONS.EMPTY_STATE')
            }}
          </p>
        </template>
      </div>
      <div class="flex items-center justify-between w-full mt-1">
        <Button
          icon="i-lucide-chevron-left"
          variant="ghost"
          color="slate"
          size="sm"
          class="!w-8 !h-6"
          :disabled="currentPage === 1"
          @click="currentPage--"
        />
        <span class="text-xs text-n-slate-11 tabular-nums">
          {{ currentPage }} / {{ totalPages }}
        </span>
        <Button
          icon="i-lucide-chevron-right"
          variant="ghost"
          color="slate"
          size="sm"
          class="!w-8 !h-6"
          :disabled="currentPage === totalPages"
          @click="currentPage++"
        />
      </div>
    </div>
    <WhatsappTemplate
      v-if="selectedTemplate"
      :template="selectedTemplate"
      :selected-contact="selectedContact"
      @send-message="handleSendMessage"
      @back="handleBack"
    />
  </div>
</template>
